import { useIntl, FormattedMessage } from 'react-intl';
import { history, useParams, useSelector, useDispatch } from 'umi';
import { useState, useMemo } from 'react';
import { useRequest, useMount } from 'ahooks';
import { Row, Col, Spin } from 'antd';
import { Button } from 'iglooform';
import moment from 'moment';
import classnames from 'classnames';
import useMobile from '@/hooks/useMobile';
import FoldCard from '@/components/fold-card';
import Amount from '@/components/amount';
import ListItemPanel from '@/components/list-item-panel';
import ListItem from '@/components/list-item';
import ModalNote from '@/components/modal-note';
import AmountWithUnit from '@/components/amount-with-unit';
import { getSession } from '@/utils/common';
import useVehicleFiledsVisiblity from '@/hooks/useVehicleFiledsVisiblity';
import useHandleFormData from '@/hooks/useHandleFormData';
import specialIntlMessages from '@/utils/specialIntlMessages';
import * as SESSION_KEYS from '@/configs/session-storage-keys';
import numeral from 'numeral';
import { get } from 'lodash';
import { images } from '@/assets/images';
import { isUndefined } from '@/utils/common';
import premiumConfig from '@/configs/premium';
import { UpdateQuote, CreateShare, QuotationConfirm } from './service';
import './index.less';
interface Props {
  productType: TTypes;
}

const Confirmation = (props: Props) => {
  const { planCmsDetail } = useSelector<ReduxState, QuoteState>((state) => ({
    ...state.quote,
  }));
  const isMobile = useMobile();
  const { productType } = props;
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const params = useParams<{ type: string }>();

  const { update } = useHandleFormData();

  const vehicleInfo = getSession(SESSION_KEYS.FIRST_STEP_INFO_FAST_QUOTE);
  const planInfo = getSession(SESSION_KEYS.PICKED_PLAN_INFO);
  const quoteInfoInit = getSession(SESSION_KEYS.QUOTE_INFO);

  const coverageType = planInfo?.coverageType ?? productType;

  // const [addonModalVisible, setAddonModalVisible] = useState(false);
  const [noteVisible, setNoteVisible] = useState(false);

  const [quoteInfo, setQuoteInfo] = useState(quoteInfoInit);

  const getPlanCmsDetail = () =>
    dispatch({
      type: 'quote/getPlanCmsDetail',
      payload: {
        productType: params.type,
        productId: planInfo?.productKey,
        planId: planInfo?.planKey,
      },
    });

  useMount(() => {
    planInfo && getPlanCmsDetail();
  });

  const visibilities = useVehicleFiledsVisiblity({
    vehicleType: vehicleInfo?.vehicle_type,
    coverageType,
  });

  const premiumInfo: { [prop: string]: any } = quoteInfo?.meta;
  const specificPremiumConfig = premiumConfig[productType][coverageType];

  const basicPremium = get(premiumInfo, specificPremiumConfig?.basicPremium);
  const paPremium = get(premiumInfo, specificPremiumConfig?.paPremium, 0);
  const vatPremium = get(premiumInfo, specificPremiumConfig?.vatPremium, null);
  const grossPremium = numeral(basicPremium).add(paPremium).value() as number;
  const amountPayable = get(
    premiumInfo,
    specificPremiumConfig?.amountPayable,
    0,
  );

  const fastNote = quoteInfo?.fast_quote?.note;

  const insurer = planCmsDetail?.cms?.data?.attributes?.insurer;
  const logoUrl =
    insurer?.data?.attributes?.logo?.data?.attributes?.url ||
    planInfo?.planIcon;
  const planName =
    planCmsDetail?.cms?.data?.attributes?.planName || planInfo?.planName;

  const { run: updateQuote, loading } = useRequest(UpdateQuote, {
    manual: true,
    onSuccess: (res) => {
      if (res) {
        setQuoteInfo(res);
      }
    },
  });

  const { run: quotationConfirm, loading: statusChange } = useRequest(
    QuotationConfirm,
    {
      manual: true,
      onSuccess: () => {
        history.replace({
          pathname: `/home/${productType}/application`,
          query: {
            productKey: planInfo?.productKey,
            planKey: planInfo?.planKey,
            quotationId: quoteInfo?.id,
          },
        });

        //event tracking: start_application
        window.gtag('event', 'start_application', {
          page: 'direct purchase',
          product_key: planInfo?.productKey,
          plan_key: planInfo?.planKey,
          quote_id: quoteInfo?.id,
          user_unique_id: JSON.parse(localStorage.getItem('user_info') ?? '{}')
            ?.id,
        });
      },
    },
  );

  const { run: createShare } = useRequest(CreateShare, {
    manual: true,
    onSuccess: (res) => {
      const url =
        location.origin +
        `/home/${params.type}/share?share_id=${
          res.share_id
        }&share_url=${encodeURIComponent(
          res.url,
        )}&replace_pathname=${encodeURIComponent('/home')}`;
      window.open(url, '_blank');
    },
  });

  const updateQuotetation = ({
    note,
    includePa,
  }: {
    note?: string;
    includePa?: boolean;
  }) => {
    const updateJson: { [prop: string]: any } = {};
    if (typeof note !== 'undefined') {
      updateJson['note'] = note;
    }
    if (typeof includePa !== 'undefined') {
      updateJson['include_pa'] = includePa ? 'Yes' : 'No';
    }
    updateQuote(
      productType,
      planInfo?.productKey,
      planInfo?.planKey,
      quoteInfo?.id,
      {
        fast_quote: update(updateJson, quoteInfo?.fast_quote),
      },
    );
  };

  const cardTitleMaps = {
    motor:
      planCmsDetail?.soi_category === 'motorcycle'
        ? formatMessage({
            id: 'Motorcycle Details',
          })
        : formatMessage({
            id: 'Vehicle Details',
          }),
    travel: formatMessage({
      id: 'Travel Details',
    }),
    pa: formatMessage({
      id: 'Policy Details',
    }),
    health: formatMessage({
      id: 'Policy Details',
    }),
  };
  const iconMaps = {
    motor:
      planCmsDetail?.soi_category === 'motorcycle'
        ? images.motorcycle_icon
        : images.vehicle_icon,
    travel: images.travel_icon,
    pa: images.pa_icon,
    health: images.hc_icon,
  };

  const foldCardList = [
    // motor
    {
      // !!! 多语言
      label: formatMessage({ id: 'Vehicle Type' }),
      value: quoteInfo?.fast_quote?.vehicle_type ? (
        <FormattedMessage
          {...specialIntlMessages[quoteInfo?.fast_quote?.vehicle_type]}
        />
      ) : null,
      hidden: isUndefined(quoteInfo?.fast_quote?.vehicle_type),
    },
    {
      // !!! 多语言
      label: formatMessage({ id: 'Business Usage' }),
      value: quoteInfo?.fast_quote?.business_usage ? (
        <FormattedMessage
          {...specialIntlMessages[quoteInfo?.fast_quote?.business_usage]}
        />
      ) : null,
      hidden: !visibilities.business,
    },
    {
      label: formatMessage({ id: 'Number of Seats' }),
      value: quoteInfo?.fast_quote?.seats_number,
      hidden: !visibilities.numberOfSeas,
    },
    {
      label: formatMessage({
        id: 'Personal Accident Insurance for driver and passengers',
      }),
      value: quoteInfo?.fast_quote?.pa_persons_num || 'No',
      hidden: !quoteInfo?.fast_quote?.include_pa,
    },
    {
      label: formatMessage({ id: 'Vehicle Value' }),
      value: (
        <AmountWithUnit
          amount={quoteInfo?.fast_quote?.vehicle_value ?? 0}
          unit={formatMessage({ id: 'VND' })}
        />
      ),
      hidden: isUndefined(quoteInfo?.fast_quote?.vehicle_value),
    },
    {
      // !!! 多语言
      label: formatMessage({ id: 'Vehicle Origin' }),
      value: quoteInfo?.fast_quote?.vehicle_origin ? (
        <FormattedMessage
          {...specialIntlMessages[quoteInfo?.fast_quote?.vehicle_origin]}
        />
      ) : null,
      hidden: isUndefined(quoteInfo?.fast_quote?.vehicle_origin),
    },
    {
      label: formatMessage({ id: 'Vehicle Manufacture Date' }),
      value: quoteInfo?.fast_quote?.manufacture_date
        ? moment(quoteInfo?.fast_quote?.manufacture_date).format(
            'DD / MM / YYYY',
          )
        : undefined,
      hidden:
        isUndefined(quoteInfo?.fast_quote?.manufacture_date) ||
        quoteInfo?.fast_quote?.manufacture_date === 0,
    },
    {
      label: formatMessage({ id: 'Vehicle Registration Date' }),
      value: quoteInfo?.fast_quote?.registration_date
        ? moment(quoteInfo?.fast_quote?.registration_date).format('MM / YYYY')
        : undefined,
      hidden:
        isUndefined(quoteInfo?.fast_quote?.registration_date) ||
        quoteInfo?.fast_quote?.registration_date === 0,
    },
    // travel
    {
      // !!! 多语言
      label: formatMessage({ id: 'Destination' }),
      value: quoteInfo?.fast_quote?.destination ? (
        <FormattedMessage
          {...specialIntlMessages[quoteInfo?.fast_quote?.destination]}
        />
      ) : null,
      hidden: isUndefined(quoteInfo?.fast_quote?.destination),
    },
    {
      label: formatMessage({ id: 'Travel Start Date' }),
      value: quoteInfo?.fast_quote?.travel_start_date
        ? moment(quoteInfo?.fast_quote?.travel_start_date).format(
            'DD / MM / YYYY',
          )
        : undefined,
    },
    {
      label: formatMessage({ id: 'Travel End Date' }),
      value: quoteInfo?.fast_quote?.travel_end_date
        ? moment(quoteInfo?.fast_quote?.travel_end_date).format(
            'DD / MM / YYYY',
          )
        : undefined,
    },
    // health
    {
      // !!! 多语言
      label: formatMessage({ id: 'Policy Duration' }),
      value: quoteInfo?.fast_quote?.policy_duration ? (
        <FormattedMessage
          {...specialIntlMessages[quoteInfo?.fast_quote?.policy_duration]}
        />
      ) : null,
      hidden: isUndefined(quoteInfo?.fast_quote?.policy_duration),
    },
    {
      label: formatMessage({ id: 'No. of Insured' }),
      value: quoteInfo?.fast_quote?.insured_num
        ? quoteInfo?.fast_quote?.insured_num
        : null,
      hidden: isUndefined(quoteInfo?.fast_quote?.insured_num),
    },
    // pa
    {
      // !!! 多语言
      label: formatMessage({ id: 'Plan' }),
      value: quoteInfo?.fast_quote?.applicants ? (
        <FormattedMessage
          {...specialIntlMessages[quoteInfo?.fast_quote?.applicants]}
        />
      ) : null,
      hidden: isUndefined(quoteInfo?.fast_quote?.applicants),
    },
  ].filter((e) => !isUndefined(e.value) && !e.hidden);

  const premiumList: any[] = [
    {
      label: formatMessage({ id: 'Basic Premium' }),
      value: <Amount amount={basicPremium} />,
    },
    {
      label: formatMessage({ id: 'Add-ons Sub-total' }),
      value: <Amount amount={paPremium} />,
      children:
        paPremium > 0
          ? [
              {
                label: formatMessage({ id: 'Personal Accident' }),
                value: <Amount amount={paPremium} />,
              },
            ]
          : [],
      hidden:
        coverageType === 'cpd' ||
        ['travel', 'health', 'pa'].includes(productType),
    },
    {
      label: formatMessage({ id: 'Total Gross Premium' }),
      value: <Amount amount={grossPremium} />,
      type: 'strong',
    },
    {
      label: formatMessage({ id: 'VAT(10%)' }),
      value: <Amount amount={vatPremium} />,
      hidden: vatPremium === null,
    },
    {
      label: formatMessage({ id: 'Amount Payable' }),
      value: <Amount amount={amountPayable} />,
      type: 'total',
      awalysShow: true,
    },
  ].filter((e) => !e.hidden);

  const noteInitValue = useMemo(
    () => ({
      note: fastNote,
    }),
    [fastNote],
  );

  const benefits = planCmsDetail?.cms?.data?.attributes?.benefits ?? [];

  const benefitsFieldList = benefits.map((item: { [prop: string]: any }) => {
    return {
      label: item?.text,
      value: item?.coverage,
      children: item.subItems.map((e: { [prop: string]: any }) => {
        return {
          label: e.text,
          value: e?.coverage,
        };
      }),
    };
  });

  const showCoverageColumn = benefitsFieldList.some((item: any) => {
    return item.value || item?.children?.some((e: any) => e.value);
  });

  const planInfoList = [
    {
      label: formatMessage({ id: 'Benefit' }),
      type: 'strong',
      value: showCoverageColumn ? formatMessage({ id: 'Coverage' }) : null,
    },
    ...benefitsFieldList,
    {
      label: formatMessage({ id: 'Add-ons Benefits' }),
      children:
        quoteInfo?.fast_quote?.include_pa === 'Yes'
          ? planCmsDetail?.soi_category === 'motorcycle'
            ? [
                {
                  label: formatMessage({
                    id: 'Personal Accident',
                  }),
                  value: formatMessage({
                    id: 'Up to VND 10.000.000',
                  }),
                },
              ]
            : [
                {
                  label: formatMessage({
                    id: 'Personal Accident (Passenger)',
                  }),
                  value: formatMessage({
                    id: 'Up to VND 150.000.000',
                  }),
                },
                {
                  label: formatMessage({
                    id: 'Personal Accident (Driver)',
                  }),
                  value: formatMessage({
                    id: 'Up to VND 10.000.000',
                  }),
                },
              ]
          : [],
      style: { marginTop: 12 },
      hidden:
        coverageType === 'cpd' ||
        ['travel', 'health', 'pa'].includes(productType),
    },
  ].filter((e) => !e.hidden);

  const coverage =
    productType === 'health' && quoteInfo?.fast_quote?.policy_duration
      ? formatMessage(
          {
            id: '{duration} Coverage',
          },
          {
            duration: (
              <FormattedMessage
                {...specialIntlMessages[quoteInfo?.fast_quote?.policy_duration]}
              />
            ),
          },
        )
      : undefined;

  return (
    <div
      className={classnames([
        'confirmationContainer',
        {
          'is-mobile': isMobile,
        },
      ])}
    >
      <FoldCard
        isExtend={false}
        noExtendIcon={false}
        title={cardTitleMaps[productType]}
        titleIconSrc={iconMaps[productType]}
        list={foldCardList}
      />

      <Spin spinning={loading || statusChange}>
        <Row className="priceInfo" gutter={[24, 24]}>
          <Col span={isMobile ? 24 : 12}>
            <ListItem
              icon={logoUrl}
              title={planName}
              list={planInfoList}
              coverage={coverage}
            />

            <Row
              className="discountBox"
              justify="space-between"
              align="middle"
              gutter={[0, 8]}
            >
              <Col>
                <span className="discountTitle">
                  {formatMessage({ id: 'Add Note' })}
                </span>
              </Col>
              <Col>
                <Button type="link" onClick={() => setNoteVisible(true)}>
                  {formatMessage({ id: 'Modify' })}
                </Button>
              </Col>
              {fastNote && (
                <Col span={24} className="discountNote">
                  {fastNote}
                </Col>
              )}
            </Row>
          </Col>

          <Col span={isMobile ? 24 : 12}>
            <ListItemPanel
              title={formatMessage({
                id: formatMessage({ id: 'Premium Details' }),
              })}
              subTitle={formatMessage({
                id: formatMessage({ id: 'All premiums in Vietnamese Dong' }),
              })}
              list={premiumList}
            />
          </Col>
        </Row>
      </Spin>

      <Row className="bottomButton" gutter={[24, 24]}>
        <Col {...(isMobile ? { span: 24 } : {})}>
          <Button
            type="primary"
            onClick={() => {
              quotationConfirm(
                productType,
                planInfo?.productKey,
                planInfo?.planKey,
                quoteInfo?.id,
              );
            }}
          >
            {formatMessage({ id: 'Complete Application' })}
          </Button>
        </Col>
        <Col {...(isMobile ? { span: 24 } : {})}>
          <Button
            onClick={() => {
              createShare(
                productType,
                planInfo?.productKey,
                planInfo?.planKey,
                quoteInfo?.id,
              );

              //event tracking: share quote
              window.gtag('event', 'share_quote', {
                page: 'fast_quote',
                channel: 'link',
                quote_id: quoteInfo?.id,
                product_key: quoteInfo?.product_key,
                plan_key: quoteInfo?.plan_key,
                user_unique_id: JSON.parse(
                  localStorage.getItem('user_info') ?? '{}',
                )?.id,
              });
            }}
          >
            {formatMessage({ id: 'Share' })}
          </Button>
        </Col>
      </Row>

      {/* <AddonList
        visible={addonModalVisible}
        onCancel={() => setAddonModalVisible(false)}
        addonList={[
          {
            add_on_id: 1,
            enable: quoteInfo?.fast_quote?.include_pa === 'Yes',
            option: {},
          },
        ]}
        addonOptions={{}}
        onConfirm={(newAddOn) => {
          if (newAddOn.length < 1) return;
          updateQuotetation({ includePa: newAddOn[0]?.enable });
        }}
      /> */}

      <ModalNote
        initValue={noteInitValue}
        visible={noteVisible}
        onCancel={() => setNoteVisible(false)}
        onConfirm={(values) => {
          updateQuotetation({ note: values?.note });
        }}
      />
    </div>
  );
};

export default Confirmation;
